/**
 * Alert — inline, in-flow message (theme T5c §2.1).
 *
 * Distinct from Toast (floats over content) and Banner (sticky page-level).
 * Variants carry semantic meaning — pick by urgency, not aesthetics.
 *
 * For the truncation-warning pattern the audit flagged (B-2), use
 * `variant="warning"` with the matching heading + body from the spec.
 */
import { CheckCircle2, Info, TriangleAlert, X, XOctagon } from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';

import { cn } from './cn';
import { IconButton } from './IconButton';

export type AlertVariant = 'info' | 'success' | 'warning' | 'error';

export interface AlertProps {
  variant?: AlertVariant;
  title?: ReactNode;
  children?: ReactNode;
  /** Render a close (×) button — calls this when clicked. */
  onDismiss?: () => void;
  /** Action row under the body. */
  action?: ReactNode;
  className?: string;
}

const VARIANTS: Record<
  AlertVariant,
  {
    bg: string;
    stripe: string;
    heading: string;
    icon: ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;
    iconClass: string;
  }
> = {
  info: {
    bg: 'bg-brand-primary-50',
    stripe: 'bg-brand-primary-600',
    heading: 'text-brand-primary-900',
    icon: Info,
    iconClass: 'text-brand-primary-600',
  },
  success: {
    bg: 'bg-success-50',
    stripe: 'bg-success-600',
    heading: 'text-success-900',
    icon: CheckCircle2,
    iconClass: 'text-success-600',
  },
  warning: {
    bg: 'bg-brand-accent-50',
    stripe: 'bg-brand-accent-600',
    heading: 'text-brand-accent-900',
    icon: TriangleAlert,
    iconClass: 'text-brand-accent-600',
  },
  error: {
    bg: 'bg-error-50',
    stripe: 'bg-error-600',
    heading: 'text-error-900',
    icon: XOctagon,
    iconClass: 'text-error-600',
  },
};

export function Alert({
  variant = 'info',
  title,
  children,
  onDismiss,
  action,
  className,
}: AlertProps) {
  const v = VARIANTS[variant];
  const Icon = v.icon;

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-lg border border-transparent p-4',
        v.bg,
        'pl-5', // extra for the left stripe
        className,
      )}
      role="alert"
    >
      <span aria-hidden className={cn('absolute inset-y-0 left-0 w-1', v.stripe)} />
      <div className="flex items-start gap-3">
        <Icon
          aria-hidden
          className={cn('mt-0.5 shrink-0', v.iconClass)}
          size={20}
          strokeWidth={1.75}
        />
        <div className="min-w-0 flex-1 space-y-2">
          {title ? <p className={cn('text-sm font-semibold', v.heading)}>{title}</p> : null}
          {children ? <div className="text-sm text-slate-700">{children}</div> : null}
          {action ? <div className="pt-1">{action}</div> : null}
        </div>
        {onDismiss ? (
          <IconButton
            aria-label="Dismiss"
            icon={X}
            size="sm"
            variant="tertiary"
            onClick={onDismiss}
          />
        ) : null}
      </div>
    </div>
  );
}
